import type { ReelItem, ReelItemProperty, ShortsEntryContext } from './types';
import type { UseToggleFavoriteOptions } from '~/composables/useToggleFavoriteSync';
import type {
	CtaViewMoreClicked,
	FormPropertyClicked,
	SharePropertyClicked,
	ShortsViewedEntry,
	ShortsViewedExit
} from '~/types/mixpanel';

import { MixpanelProps } from '~/core/service/mixpanel';
import { hasShortsVideo } from '~/utils';

function getAgentDisplayName(property: ReelItemProperty): string {
	const agent = property.agent;
	if (!agent) return '';
	return 'displayName' in agent && agent.displayName
		? agent.displayName
		: agent.name;
}

function getCompanyName(property: ReelItemProperty): string {
	return property.company?.name ?? '';
}

/** Maps a `ShortsEntryContext` to the `inicial_*` fields used by Mixpanel. */
function entryToInicialProps(entry: ShortsEntryContext) {
	return {
		inicialCommercialId: entry.entryCommercialId,
		inicialAskingPrice: entry.entryAskingPrice,
		inicialRegionName: entry.entryRegionName
	} as const;
}

/** Maps a `ShortsEntryContext` to the `MixpanelProps.INICIAL_*` fields. */
function entryToMixpanelInicialProps(entry: ShortsEntryContext) {
	return {
		[MixpanelProps.INICIAL_COMMERCIAL_ID]: entry.entryCommercialId,
		[MixpanelProps.INICIAL_ASKING_PRICE]: entry.entryAskingPrice,
		[MixpanelProps.INICIAL_REGION_NAME]: entry.entryRegionName
	} as const;
}

/**
 * Builds the event-data object passed to `useToggleFavoriteSync` on the Shorts surface.
 */
export function buildShortsFavoriteEventData(input: {
	property: ReelItemProperty;
	currentPage: string;
	companyName: string;
	agentName: string;
	entry?: ShortsEntryContext;
}): UseToggleFavoriteOptions {
	const { property, entry } = input;
	return {
		currentPage: input.currentPage,
		tagExclusive: Boolean(property.isExclusive),
		companyName: input.companyName,
		agentName: input.agentName,
		clickCard: true,
		clickProperty: false,
		videoShorts: hasShortsVideo(property),
		askingPrice: property.askingPrice ?? null,
		regionName: property.region ?? '',
		...(entry && entryToInicialProps(entry))
	};
}

/**
 * Payload for `form_property_clicked` when the contact modal opens on the Shorts surface.
 */
export function buildShortsFormPropertyClickedPayload(input: {
	property: ReelItemProperty;
	currentPage: string;
	isLoggedIn: boolean;
	entry: ShortsEntryContext;
}): FormPropertyClicked {
	const { property, entry } = input;
	return {
		[MixpanelProps.CTA_TEXT]: 'Falar com corretor',
		[MixpanelProps.SECTION_NAME]: 'feed shorts',
		[MixpanelProps.COMMERCIAL_ID]: property.commercialId,
		[MixpanelProps.COMPANY_NAME]: getCompanyName(property),
		[MixpanelProps.AGENT_NAME]: getAgentDisplayName(property),
		[MixpanelProps.CURRENT_PAGE]: input.currentPage,
		[MixpanelProps.USER_STATUS]: input.isLoggedIn ? 'logged in' : 'logged out',
		[MixpanelProps.ASKING_PRICE]: property.askingPrice ?? undefined,
		[MixpanelProps.REGION_NAME]: property.region ?? '',
		...entryToMixpanelInicialProps(entry)
	};
}

/**
 * Extra properties for `share_property_clicked` on the Shorts surface (per analytics spec).
 */
export function buildShortsSharePropertyPayload(
	property: ReelItemProperty,
	entry: ShortsEntryContext
): Partial<SharePropertyClicked> {
	return {
		[MixpanelProps.SECTION_NAME]: 'shorts',
		[MixpanelProps.COMMERCIAL_ID]: property.commercialId,
		[MixpanelProps.ASKING_PRICE]: property.askingPrice ?? null,
		[MixpanelProps.REGION_NAME]: property.region ?? '',
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(property),
		...entryToMixpanelInicialProps(entry)
	};
}

/** Payload for `shorts_viewed_entry` when a reel becomes active. */
export function buildShortsViewedEntryPayload(input: {
	item: ReelItem;
	index: number;
	currentPagePath: string;
	entry: ShortsEntryContext;
	recommendationCountVideos?: number;
	recommendationCountPhotos?: number;
}): ShortsViewedEntry {
	const { item, index, currentPagePath, entry } = input;
	const p = item.property;
	const hasVideo = item.kind === 'video';

	return {
		[MixpanelProps.COMMERCIAL_ID]: p.commercialId,
		[MixpanelProps.CURRENT_PAGE]: currentPagePath,
		[MixpanelProps.SHORT_POSITION]: index + 1,
		[MixpanelProps.SHORT_TYPE]: hasVideo ? 'video' : 'image',
		[MixpanelProps.ASKING_PRICE]: p.askingPrice ?? null,
		[MixpanelProps.REGION_NAME]: p.region ?? '',
		[MixpanelProps.COMPANY_NAME]: getCompanyName(p),
		[MixpanelProps.AGENT_NAME]: getAgentDisplayName(p),
		[MixpanelProps.TAG_EXCLUSIVE]: Boolean(p.isExclusive),
		[MixpanelProps.VIDEO_SHORTS]: hasVideo,
		[MixpanelProps.PHOTO_SHORTS]: !hasVideo && Boolean(item.thumbnailUrl),
		...entryToMixpanelInicialProps(entry),
		[MixpanelProps.RECOMMENDATION_SHORTS_COUNT_VIDEOS]:
			input.recommendationCountVideos,
		[MixpanelProps.RECOMMENDATION_SHORTS_COUNT_PHOTOS]:
			input.recommendationCountPhotos
	};
}

/**
 * Below this, an exit caused by a property swap is the virtualized list
 * replacing the item during mount, not a viewer leaving.
 */
export const MOUNT_SWAP_MS = 300;

/**
 * Whether an exit event should be dropped as a mount artifact.
 *
 * The feed mounts with a placeholder property and the virtualized list swaps
 * in the clicked reel milliseconds later, on the same live component. That
 * swap looks exactly like "user left this short" to the watcher. Measured over
 * 3 months, it accounted for 28% of all exit events, every one of them in the
 * sub-second bucket, which made the metric read as mass abandonment.
 *
 * Only the exit is dropped. The swapped-in property still fires its entry, or
 * the short the user actually watches would report neither entry nor exit.
 */
export function isMountSwapExit(
	propertyChanged: boolean,
	elapsedMs: number
): boolean {
	return propertyChanged && elapsedMs < MOUNT_SWAP_MS;
}

/** Payload for `shorts_viewed_exit` when the user switches away from a reel. */
/**
 * Dwell time in whole seconds.
 *
 * Deliberately kept at the precision this property has always had: three
 * months of history are integers, and switching mid-series would move every
 * average and explode the cardinality of any saved grouping, for no analytical
 * gain. Sub-second wait time is answered by `attempt_ms` on the video_qoe
 * action, which measures it in milliseconds and is where that analysis lives.
 */
export function dwellSeconds(elapsedMs: number): number {
	return Math.round(elapsedMs / 1000);
}

export function buildShortsViewedExitPayload(input: {
	item: ReelItem;
	index: number;
	currentPagePath: string;
	entry: ShortsEntryContext;
	elapsedMs: number;
	recommendationCountVideos?: number;
	recommendationCountPhotos?: number;
}): ShortsViewedExit {
	return {
		...buildShortsViewedEntryPayload(input),
		[MixpanelProps.SCREEN_TIME]: dwellSeconds(input.elapsedMs)
	};
}

export function buildFeedEndViewedEntryPayload(input: {
	currentPagePath: string;
	position: number;
	feedEnd: 'filters' | 'reset';
}): ShortsViewedEntry {
	return {
		[MixpanelProps.COMMERCIAL_ID]: 'N/A',
		[MixpanelProps.CURRENT_PAGE]: input.currentPagePath,
		[MixpanelProps.SHORT_POSITION]: input.position,
		[MixpanelProps.SHORT_TYPE]: 'image',
		[MixpanelProps.FEED_END]: input.feedEnd
	};
}

export function buildFeedEndViewedExitPayload(input: {
	currentPagePath: string;
	position: number;
	feedEnd: 'filters' | 'reset';
	elapsedMs: number;
}): ShortsViewedExit {
	return {
		...buildFeedEndViewedEntryPayload(input),
		[MixpanelProps.SCREEN_TIME]: dwellSeconds(input.elapsedMs)
	};
}

/** Payload for `cta_view_more_clicked` when "Ver detalhes" is clicked on a reel. */
export function buildShortsCtaViewMorePayload(input: {
	property: ReelItemProperty;
	currentPage: string;
	destinationUrl: string;
	entry: ShortsEntryContext;
}): CtaViewMoreClicked {
	const { property, entry } = input;
	return {
		[MixpanelProps.CURRENT_PAGE]: input.currentPage,
		[MixpanelProps.SECTION_NAME]: 'feed shorts',
		[MixpanelProps.DESTINATION_URL]: input.destinationUrl,
		[MixpanelProps.CTA_TEXT]: 'Ver detalhes',
		[MixpanelProps.COMMERCIAL_ID]: property.commercialId,
		[MixpanelProps.ASKING_PRICE]: property.askingPrice ?? null,
		[MixpanelProps.REGION_NAME]: property.region ?? '',
		...entryToMixpanelInicialProps(entry)
	};
}

/** Payload for `cta_view_more_clicked` when "Explorar agora" is clicked on the onboarding overlay. */
export function buildShortsOnboardingCtaPayload(input: {
	property: ReelItemProperty | null;
	currentPage: string;
	entry: ShortsEntryContext;
}): CtaViewMoreClicked {
	const { property, entry } = input;
	return {
		[MixpanelProps.CURRENT_PAGE]: input.currentPage,
		[MixpanelProps.SECTION_NAME]: 'feed shorts onboarding',
		[MixpanelProps.DESTINATION_URL]: input.currentPage,
		[MixpanelProps.CTA_TEXT]: 'Explorar agora',
		[MixpanelProps.COMMERCIAL_ID]: property?.commercialId ?? '',
		[MixpanelProps.ASKING_PRICE]: property?.askingPrice ?? null,
		[MixpanelProps.REGION_NAME]: property?.region ?? '',
		...entryToMixpanelInicialProps(entry)
	};
}
